// "Somebody changed a product" → drop every catalogue cache this tab is holding, now.
//
//   Wired once from apps/index.ts (startCatalogSignal), like startKeepalive().
//
// WHAT THIS IS FOR. Every screen that draws a product caches it, because the reads are big and the
// data changes when a merchandiser edits the catalogue rather than per transaction. The layers and
// what they cost an operator who just changed something:
//
//   RefData memo              5 min    brands, categories, storefrontVariants, the barcode master
//   catalogCache              30 s     /store/catalog, per branch+surface
//   Cache-Control: max-age    120 s    the browser's OWN copy of /store/catalog
//
// Those add up, and they are per TAB: the till that needs to see the change is not the tab that
// made it. So the server announces the write on the realtime bus (helpers/realtime/topics.ts →
// the `catalog` topic) and every connected tab lands here and throws its copies away. The next
// read a screen does goes to the network, and the page it draws is the one the operator saved.
//
// SIGNAL, NOT DATA — the same contract the rest of `/rt` keeps. Nothing about what a screen loads
// changes, only when. Nothing is re-fetched HERE: this drops the caches, then tells whoever asked
// through onCatalogChange(). Who asks, and why only the pane on screen:
//
//   ShellComponent / StaffApp   the VISIBLE pane, through refreshWhenIdle() — a hidden pane
//                               re-reads when its shell shows it again anyway
//   pos-catalog, staffassist,   themselves — each already had a background refresh that
//   staffpick, staffstockpick   reconciles rather than rebuilds, so the signal just runs it now
//
// Emptying a cache repaints nothing by itself. Before those listeners, a pane that had drawn its
// list kept drawing it until somebody reloaded the page — which is what "it only changes after
// F5" was.
//
// `selfEcho: true` ON PURPOSE, against the usual rule. Echo suppression exists so a tab does not
// refetch the write it just made — correct when the writer already has the answer in hand. Here it
// does not: the Product List tab that saved a product holds the SAME stale `RefData` and
// `catalogCache` as every other tab, and skipping it would leave the one screen whose operator is
// actually watching showing the old catalogue for five minutes. Dropping a cache is cheap and
// idempotent; there is nothing to suppress.
//
// FAIL-SOFT. `rt` never throws and reconnects on its own; a dead socket means the TTLs above are
// what expire the caches, which is exactly the behaviour before this existed. And a catalogue
// write made outside BrandOS announces nothing at all — the TTLs are still the floor under this,
// not a thing this replaces.
import { rt } from "./realtime.js";
import { isIdentitySurface } from "./reauth.js";
import { invalidate as invalidateRefData } from "./refdata.js";
import { invalidateCatalogCache } from "./media.js";
/** The topic the server publishes on a catalogue write — keep in step with helpers/realtime/topics.ts. */
export const CATALOG_TOPIC = "catalog";
const listeners = new Set();
/**
 * Run `fn` each time the catalogue changes, AFTER this tab's caches are gone — so a read `fn`
 * starts is a network read. Returns the unsubscribe.
 */
export function onCatalogChange(fn) {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
}
function dropCatalogCaches() {
    // Whole-catalogue reads with no per-material granularity, so this is all-or-nothing by
    // construction — see the topic's own note on why there is no `catalog:<materialNo>`.
    invalidateRefData();
    // Also bumps `mediaBust`, which is what gets past `Cache-Control: max-age=120` on
    // /store/catalog — clearing the in-memory map alone would let the browser hand back the
    // pre-write body from its own store.
    invalidateCatalogCache();
    for (const fn of [...listeners]) {
        try {
            fn();
        }
        catch { /* one pane's refresh must not stop the next one's */ }
    }
}
// ---- Refreshing a pane without losing what somebody is typing ----------------------------------
/** An open dialog, in any of the three spellings the panes use. */
const OVERLAY_OPEN = ".modal-bg.open, .scrim.open, .modalLayer.show";
/** Fields that only narrow a list. Focus sitting in one is not work in progress — and a till's
 *  scan box keeps focus all day, so counting it would mean that screen never refreshes. */
const FILTER_FIELD = ".search-inp, .filt-sel, input[type=search]";
const RETRY_MS = 5_000;
/**
 * Is somebody mid-task in this pane? A re-read rebuilds the pane from the server, so doing it
 * under an open dialog, or under a cursor in a quantity field, throws their typing away.
 */
export function paneBusy(pane, doc = globalThis.document) {
    if (pane.query(OVERLAY_OPEN))
        return true;
    const root = pane.getRootElement();
    const el = doc?.activeElement;
    if (!root || !el || !root.contains(el))
        return false;
    const editable = el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
    return editable && !el.matches(FILTER_FIELD);
}
const waiting = new WeakSet();
/**
 * Re-read `pane` now, or as soon as nobody is working in it.
 *
 * Busy → try again every RETRY_MS. Gone from screen meanwhile → drop it: the shell re-reads a pane
 * every time it shows it, so the change is picked up there without this having to watch.
 * One pending retry per pane, however many writes land while it waits.
 */
export function refreshWhenIdle(pane, run) {
    if (waiting.has(pane))
        return;
    waiting.add(pane);
    const attempt = () => {
        const root = pane.getRootElement();
        // `:if` hides with display:none, which leaves an element with no boxes.
        if (!root || root.getClientRects().length === 0) {
            waiting.delete(pane);
            return;
        }
        if (paneBusy(pane)) {
            setTimeout(attempt, RETRY_MS);
            return;
        }
        waiting.delete(pane);
        void Promise.resolve().then(run).catch(() => { });
    };
    attempt();
}
/**
 * Subscribe this tab to catalogue changes. Returns the unsubscribe handle; the app never calls it
 * (the subscription lives as long as the page does) but a test does.
 *
 * SIGNED-IN SURFACES ONLY, and this is a capacity rule rather than a privacy one.
 *
 * `apps/index.ts` is the entry for EVERY page, the anonymous mini app and member app included. An
 * anonymous socket connects as `scope: "display"` (modules/main/controllers/realtime.ts), and
 * those are capped at MAX_DISPLAY_CLIENTS = 32 so that an uncredentialed endpoint cannot be used
 * to hold connections open. Subscribing unconditionally would put every shopper's phone in that
 * pool: 32 of them browsing at once and the shop's wall displays get closed 1013 and drop to
 * polling — a queue board going stale because a product was renamed, which nothing on either
 * screen would explain.
 *
 * Nothing is lost by skipping them. `catalog` is not in DISPLAY_TOPICS, so an anonymous
 * subscription would be dropped silently at the server anyway; and neither app holds any of the
 * caches this clears — both read `/store/catalog` fresh, through their own short TTLs.
 *
 * Same test `startKeepalive()` uses, so "which surfaces sign in with a BrandOS identity" stays one
 * answer in one place (api/reauth.ts) rather than two lists that drift.
 */
export function startCatalogSignal() {
    const noop = () => { };
    const loc = globalThis.location;
    if (!loc || !isIdentitySurface(String(loc.pathname ?? "")))
        return noop;
    return rt.on(CATALOG_TOPIC, dropCatalogCaches, { selfEcho: true });
}
//# sourceMappingURL=catalogSignal.js.map